import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import RetainerEditor from '../components/retainers/RetainerEditor';
import RetainerDrawer from '../components/retainers/RetainerDrawer';
import { Button, Card, Input, Select, Skeleton, Tabs, useToast } from '../../design-system';
import { money, annualValue, FREQUENCY_SHORT, ordinal } from '../../lib/retainer';
import '../styles/Retainers.css';

// The firm's standing arrangements: which clients are on a retainer, what each
// one covers, and what was agreed for it.
//
// ⚠️ THE SAME DATA Finance's Retainers tab SHOWS, asking a different question.
// Finance asks "what recurring revenue do we hold" and answers in one column of
// figures; this asks "what did we agree to do for this client", which is a list
// of services, a negotiated fee and a piece of paper. One collection, two
// hosts, one editor — the PlanChooser rule. Splitting the record instead would
// have left the nightly generator with two to choose between.

const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—');

const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'all', label: 'All' },
  { key: 'clients', label: 'By client' },
];

function OrgAdminRetainers() {
  const toast = useToast();
  const [tab, setTab] = useState('active');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [freqFilter, setFreqFilter] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((n) => n + 1);

  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // `status` is a server-side filter so the totals above the table always
      // describe the rows under it. The By-client tab reads every row and
      // groups here — it is a rearrangement of the same set, not a new query.
      const status = tab === 'active' ? 'active' : '';
      const r = await apiClient.get('/api/retainers', { params: status ? { status } : {} });
      setData(r.data);
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not load retainers');
      setData({ rows: [], totals: {} });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, refreshKey]);

  useEffect(() => { load(); }, [load]);

  // Searching and the cadence filter are applied here rather than round-tripped:
  // the set is one row per arrangement, which is tens, not thousands.
  const rows = useMemo(() => {
    const all = data?.rows || [];
    const q = search.trim().toLowerCase();
    return all.filter((r) => {
      if (freqFilter !== 'all' && r.frequency !== freqFilter) return false;
      if (!q) return true;
      return [r.clientName, r.clientCode, r.title, r.agreementNumber]
        .some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [data, search, freqFilter]);

  const byClient = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const key = r.clientId;
      if (!map.has(key)) {
        map.set(key, {
          clientId: key, clientName: r.clientName, clientCode: r.clientCode,
          rows: [], annual: 0, services: 0,
        });
      }
      const g = map.get(key);
      g.rows.push(r);
      if (r.isActive) g.annual += annualValue(r.amount, r.frequency);
      g.services += r.services.length;
    });
    return [...map.values()].sort((a, b) => b.annual - a.annual);
  }, [rows]);

  const t = data?.totals || {};

  return (
    <OrgAdminLayout>
      <div className="ret-head">
        <div>
          <h2 className="ret-title">Retainers</h2>
          <p className="ret-sub">
            Clients the firm serves on a standing arrangement — a set of services, at a fee agreed once
            and billed by the calendar. Invoices are generated in arrears, once each period has ended.
          </p>
        </div>
        <Button variant="primary" onClick={() => setEditing({})}>+ New retainer</Button>
      </div>

      {loading || !data ? <Skeleton.Text lines={6} /> : (
        <>
          <div className="ret-kpis">
            <Card className="ret-kpi">
              <span className="ret-kpi-label">Clients on retainer</span>
              <span className="ret-kpi-value">{t.clients ?? 0}</span>
              <span className="ret-kpi-note">{t.active ?? 0} active arrangement{t.active === 1 ? '' : 's'}</span>
            </Card>
            <Card className="ret-kpi">
              <span className="ret-kpi-label">Recurring, per year</span>
              <span className="ret-kpi-value">{money(t.annualValue || 0)}</span>
              <span className="ret-kpi-note">Active agreements, annualised</span>
            </Card>
            {/* Worth its own tile because it appears on no other screen: only
                the agreed figure ever reaches an invoice, so what the firm gave
                away to hold this book is otherwise invisible. */}
            <Card className="ret-kpi">
              <span className="ret-kpi-label">Concessions given</span>
              <span className="ret-kpi-value">{money(t.annualConcession || 0)}</span>
              <span className="ret-kpi-note">A year, against the firm&apos;s own rate card</span>
            </Card>
            <Card className="ret-kpi">
              <span className="ret-kpi-label">Stopped</span>
              <span className="ret-kpi-value">{t.stopped ?? 0}</span>
              <span className="ret-kpi-note">No longer generating invoices</span>
            </Card>
          </div>

          <Tabs tabs={TABS} active={tab} onChange={setTab} />

          <Card>
            <div className="ret-toolbar">
              <Input
                placeholder="Search client, title or reference…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Select value={freqFilter} onChange={(e) => setFreqFilter(e.target.value)}>
                <option value="all">Every cadence</option>
                {Object.entries(FREQUENCY_SHORT).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
              <span className="ret-count">{rows.length} shown</span>
            </div>

            {tab === 'clients' ? (
              <div className="ret-scroll">
                <table className="ret-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th className="ret-num">Arrangements</th>
                      <th className="ret-num">Services</th>
                      <th className="ret-num">Per year</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {byClient.map((g) => (
                      <tr key={g.clientId}>
                        <td>
                          <span className="ret-name">{g.clientName}</span>
                          {g.clientCode && <span className="ret-code">{g.clientCode}</span>}
                        </td>
                        <td className="ret-num">{g.rows.length}</td>
                        <td className="ret-num">{g.services}</td>
                        <td className="ret-num"><b>{money(g.annual)}</b></td>
                        <td className="ret-num">
                          <Button variant="secondary" size="sm" onClick={() => setViewing(g.rows[0].id)}>Open</Button>
                        </td>
                      </tr>
                    ))}
                    {byClient.length === 0 && (
                      <tr><td colSpan={5}><p className="ret-empty">No clients on a retainer yet.</p></td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="ret-scroll">
                <table className="ret-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Client</th>
                      <th>Covers</th>
                      <th>Every</th>
                      <th className="ret-num">Fee</th>
                      <th className="ret-num">Per year</th>
                      <th>Bills on</th>
                      <th>Last billed</th>
                      <th>Agreement</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="ret-muted">{r.agreementNumber || '—'}</td>
                        <td>
                          <span className="ret-name">{r.clientName}</span>
                          {r.clientCode && <span className="ret-code">{r.clientCode}</span>}
                          <div className="ret-muted">{r.title}</div>
                        </td>
                        <td>
                          {r.services.length
                            ? <span className="ret-chip">{r.services.length} service{r.services.length === 1 ? '' : 's'}</span>
                            : <span className="ret-muted">Flat fee</span>}
                        </td>
                        <td className="ret-muted">{FREQUENCY_SHORT[r.frequency] || r.frequency}</td>
                        <td className="ret-num">
                          <b>{money(r.amount)}</b>
                          {/* Only where it says something: a fee equal to the
                              list price has no concession to report. */}
                          {r.discount > 0 && (
                            <div className="ret-disc-note">was {money(r.listTotal)}</div>
                          )}
                        </td>
                        <td className="ret-num ret-muted">{money(r.annualValue)}</td>
                        <td className="ret-muted">
                          {ordinal(r.billingDay || 1)}
                          {/* An overdue billing date means the generator has
                              not run — a retainer that quietly stopped
                              invoicing is the failure this module exists to
                              catch, so it is said on the row, not buried. */}
                          {r.nextIsOverdue
                            ? <div className="ret-overdue">due now</div>
                            : r.nextBillsOn && <div className="ret-next">next {fmtDate(r.nextBillsOn)}</div>}
                        </td>
                        <td className="ret-muted">{r.lastBilledPeriod || '—'}</td>
                        <td className="ret-muted">
                          {r.agreementGeneratedAt
                            ? `v${r.agreementVersion} · ${fmtDate(r.agreementGeneratedAt)}`
                            : 'Not generated'}
                        </td>
                        <td>
                          <span className={`ret-pill ${r.isActive ? 'ret-s-active' : 'ret-s-stopped'}`}>
                            {r.isActive ? 'Active' : 'Stopped'}
                          </span>
                        </td>
                        <td className="ret-num ret-actions">
                          <Button variant="secondary" size="sm" onClick={() => setViewing(r.id)}>Open</Button>
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={11}>
                          <p className="ret-empty">
                            {search || freqFilter !== 'all'
                              ? 'Nothing matches those filters.'
                              : 'No retainers yet. Put a client on one and their services and fee are recorded in one place.'}
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {viewing && (
        <RetainerDrawer
          retainerId={viewing}
          onClose={() => setViewing(null)}
          onEdit={(r) => { setViewing(null); setEditing(r); }}
          onChanged={bump}
        />
      )}
      {editing && (
        <RetainerEditor
          retainer={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); bump(); }}
        />
      )}
    </OrgAdminLayout>
  );
}

export default OrgAdminRetainers;
